import type { CurrencyCode, CustomAccountInput } from 'hooks/customerApi';
import { useToast } from 'components/ToastManager';
import { CurrencyMode, useSyncCustomAccountMutation } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { getResultOrError } from 'utils/errors';

import { SchemaCurrencyMatchInput } from '@fragment/schema-validation/types';
import { SafeStringSchema } from '@fragment/types';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast/Toast';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import { ToggleField } from '../../link/ToggleField';
import { transformInputToCurrency } from '../SchemasV2/EditEntry/utils';
import { SettingsModalLayout } from '../SchemasV2/WorkspaceSettings/SettingsModalLayout';

type AddCustomAccountViewProps = {
  onClose: VoidFunction;
  linkId: string;
  refresh: VoidFunction;
};

type FormInput = Omit<CustomAccountInput, 'currency'> & {
  currency: string;
};

export const AddCustomAccountView = ({
  onClose,
  linkId,
  refresh,
}: AddCustomAccountViewProps) => {
  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValid, isDirty, errors: activeError },
    // We represent the currency as a string on input and transform it to a Currency object on submit
  } = useForm<FormInput>({
    mode: 'onTouched',
    defaultValues: {
      currencyMode: CurrencyMode.Single,
      currency: 'USD',
    },
  });

  const { context } = useCustomerApiContext();
  const [, syncCustomAccountMutation] = useSyncCustomAccountMutation();
  const { showToast } = useToast();
  const createAccount = useCallback(
    async (account: FormInput) => {
      let currency;
      if (account.currencyMode === CurrencyMode.Single) {
        const currencyInput = transformInputToCurrency(account.currency);
        currency = SchemaCurrencyMatchInput.safeParse(currencyInput);
        if (!currency.success) {
          // This should never happen, but we'll show an error if it does
          showToast(<Toast type="error" message="Invalid currency" />);
          return;
        }
        currency = {
          code: currency.data.code as CurrencyCode,
          customCurrencyId: currency.data.customCurrencyId,
        };
      }

      const { data: syncCustomAccountData, error: syncCustomAccountError } =
        await syncCustomAccountMutation(
          {
            account: {
              ...account,
              currency,
            },
            link: { id: linkId },
          },
          context
        );
      const syncCustomAccountRes = getResultOrError(
        syncCustomAccountData?.syncCustomAccounts,
        syncCustomAccountError
      );
      if (syncCustomAccountRes.isError) {
        showToast(
          <Toast type="error" message="Failed to add external account" />
        );
        return;
      }

      showToast(
        <Toast type="success" message="Successfully added external account" />
      );
      refresh();
      onClose();
    },
    [context, linkId, syncCustomAccountMutation, showToast, refresh, onClose]
  );

  return (
    <SettingsModalLayout
      title="Add External Account"
      onBackButtonClick={onClose}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit(createAccount)}
      bottomButtonSection={
        <PrimaryButtonV2
          type="submit"
          disabled={isSubmitting || !isValid || !isDirty}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </PrimaryButtonV2>
      }
    >
      <div className="flex flex-col gap-y-[1px]">
        <ModalField label="Name" hasError={!!activeError.name}>
          <ModalTextInput
            {...register('name', {
              required: 'Name is required',
            })}
            placeholder="Operating Bank"
            data-testid="name-input"
            hasError={!!activeError.name}
          />
        </ModalField>
        <ModalField label="External ID" hasError={!!activeError.externalId}>
          <ModalTextInput
            {...register('externalId', {
              required: 'External ID is required',
              validate: (value) => {
                const safeString = SafeStringSchema.safeParse(value);
                if (!safeString.success) {
                  return 'Invalid external ID';
                }
                return true;
              },
            })}
            placeholder="account_1234"
            data-testid="external-id-input"
            hasError={!!activeError.externalId}
          />
        </ModalField>

        <ToggleField label="Currency">
          <Toggle
            {...register('currencyMode', {
              required: 'Currency mode is required',
            })}
            inline
            buttonPadding="p-f0"
            buttonHeight="h-f2"
            initialActiveButton="left"
            leftLabel="Single"
            rightLabel="Multi"
            onLeftClick={() =>
              setValue('currencyMode', CurrencyMode.Single, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
            onRightClick={() => {
              setValue('currencyMode', CurrencyMode.Multi, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });

              setValue('currency', '', {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });
            }}
            data-testid="currency-mode-toggle"
          />
        </ToggleField>
        {getValues('currencyMode') === CurrencyMode.Single && (
          <ModalField label="Code" hasError={!!activeError.currency}>
            <ModalTextInput
              {...register('currency', {
                validate: (value) => {
                  const currency = transformInputToCurrency(value);
                  const currencyMatchInput =
                    SchemaCurrencyMatchInput.safeParse(currency);
                  if (!currencyMatchInput.success) {
                    return 'Invalid currency';
                  }
                  return true;
                },
              })}
              placeholder="ex: USD"
              data-testid="currency-input"
            />
          </ModalField>
        )}
      </div>
    </SettingsModalLayout>
  );
};
